import { Component } from "react"

/* Redux */
import { connect } from "react-redux"

/* Components */
import { InnerLayout, Pagination, StudioFilter } from "../../components"

/* Widgets */
import { DealerForm, DealerQR } from "../../widgets/Dealer"

/* Translate */
import { t } from "../../translates/translate"

/* REST API */
import { list, remove } from "../../api/Dealer"

/* Helpers */
import { withRouter, utils } from "../../helpers"

/* Icons */
import { PlusIcon, EditIcon, TrashIcon, BarcodeIcon, SearchIcon, CloseIcon, LockIcon, StudioIcon, TableIcon, DealerIcon } from "../../widgets/Dealer/DealerIcons"

/* Sweet alert */
import Swal from "../../helpers/alert"


/* DELAY BEFORE THE SEARCH REQUEST LEAVES */
const DELAY = 400


/* INITIALS OF A NAME — THE AVATAR DRAWS THEM INSTEAD OF A FACE */
const getInitials = (name) => {

    /* Split Words */
    const words = String(name).trim().split(" ").filter((item) => item !== "")

    /* Check Words */
    if (words.length === 0) {
        return "?"
    }

    /* Send Result */
    return words.slice(0, 2).map((item) => item.charAt(0).toUpperCase()).join("")
}


/* Page Dealers */
class Dealers extends Component {

    constructor() {
        super()

        this.state = {
            data: [],
            status: "loading",
            count: 0,
            search: "",
            canEdit: false,
            studios: [],
        }
    }

    componentDidMount = () => {
        window.scrollTo(0, 0)
        this.load()
    }

    componentDidUpdate = (prevProps) => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page — a new page starts from the top with the loader */
        if (prevProps.params.page !== page) {
            window.scrollTo(0, 0)
            this.load(true)
        }

    }

    componentWillUnmount = () => {
        clearTimeout(this._timer)
    }

    /* Get Page */
    getPage = () => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page */
        if (Number.isInteger(parseInt(page))) {
            return parseInt(page)
        }

        return 1
    }

    /* Load Action */
    load = (reload = false) => {

        /* Fields */
        const { search, studios } = this.state
        const page = this.getPage()

        /* Check Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        list(page, search, utils.arrayID(studios)).then((response) => {
            if (response.status === 200) {
                this.setState((prevState) => ({
                    status: "ok",
                    data: response.data.list,
                    count: response.data.count,
                    canEdit: utils.checkBoolean(response.data.canEdit),
                    studios: prevState.studios.length > 0
                        ? prevState.studios
                        : (response.data.studios || []).map((item) => ({ ...item, checked: true })),
                }))
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: [], count: 0 })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: [], count: 0 })
            }
            else {
                this.setState({ status: "error", data: [], count: 0 })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0 })
        })

    }

    /* Change Search — the request leaves only after the typing stops */
    changeSearch = (search) => {

        this.setState({ search })

        clearTimeout(this._timer)
        this._timer = setTimeout(() => this.search(), DELAY)
    }

    /* Search Action — every search restarts from the first page */
    search = () => {

        /* Fields */
        const { navigate } = this.props

        /* Check Page */
        if (this.getPage() !== 1) {
            navigate("/dealer/1")
            return
        }

        this.load()
    }

    /* Apply Studios — the filter also restarts from the first page */
    applyStudios = () => {

        /* Fields */
        const { navigate } = this.props

        /* Check Page */
        if (this.getPage() !== 1) {
            navigate("/dealer/1")
            return
        }

        this.load(true)
    }

    /* Remove Action */
    remove = (item) => {

        Swal.fire({
            title: t("swal.deleteConfirm"),
            text: item.name,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f44336",
            reverseButtons: true,
            confirmButtonText: t("swal.deleteButton"),
            cancelButtonText: t("swal.cancelButton"),
        }).then((result) => {
            if (result.isConfirmed) {
                remove({ id: item.id }).then((response) => {
                    if (response.status === 200) {
                        this.load(true)
                        Swal.fire(utils.notification(t("dealer.deletedSuccess"), "success"))
                    }
                    else if (response.data && response.data.message) {
                        Swal.fire(utils.notification(response.data.message, "error"))
                    }
                    else if (response.status === 498) {
                        Swal.fire(utils.notification(t("swal.invalidKey"), "error"))
                    }
                    else if (response.status === 401 || response.status === 403) {
                        Swal.fire(utils.notification(t("swal.noAccess"), "error"))
                    }
                    else {
                        Swal.fire(utils.notification(t("swal.generic"), "error"))
                    }
                }).catch(() => {
                    Swal.fire(utils.notification(t("swal.generic"), "error"))
                })
            }
        })
    }

    /* Draw Search */
    _search = () => {

        /* Fields */
        const { search } = this.state

        return (
            <div className="dealer--search">

                <SearchIcon />

                <input
                    value={search}
                    onChange={(event) => this.changeSearch(event.target.value)}
                    placeholder={t("dealer.searchByName")}
                />

                {search !== "" &&
                    <div onClick={() => this.changeSearch("")} className="dealer--search-clear">
                        <CloseIcon size={12} />
                    </div>
                }

            </div>
        )
    }

    /* Draw Pin — a viewer never receives it */
    _pin = (item) => {

        /* Fields */
        const { canEdit } = this.state

        /* Hidden */
        if (!canEdit) {
            return <div className="dealer--pin hidden">••••••</div>
        }

        return (
            <div onClick={() => this._qr.open(item)} className="dealer--pin" title={t("dealer.qrCode")}>
                <BarcodeIcon size={14} />
                <span>{utils.checkValue(item.pincode, "—")}</span>
            </div>
        )
    }

    /* Draw Studio */
    _studio = (item) => {

        /* Empty Studio */
        if (!item.studio) {
            return <div className="dealer--studio-empty">{t("dealer.noStudio")}</div>
        }

        return (
            <div className="dealer--studio">
                <StudioIcon />
                <span>{item.studio}</span>
            </div>
        )
    }

    /* Draw Actions */
    _actions = (item) => {

        /* Fields */
        const { canEdit } = this.state

        /* Viewer — every change is closed */
        if (!canEdit) {
            return (
                <div className="dealer--action-locked">
                    <LockIcon />
                    <span>{t("dealer.viewOnly")}</span>
                </div>
            )
        }

        return (
            <>
                <div onClick={() => this._qr.open(item)} className="dealer--action" title={t("dealer.qrCode")}>
                    <BarcodeIcon />
                </div>

                <div onClick={() => this._form.open({ type: "edit", id: item.id })} className="dealer--action" title={t("dealer.edit")}>
                    <EditIcon />
                </div>

                <div onClick={() => this.remove(item)} className="dealer--action red" title={t("dealer.delete")}>
                    <TrashIcon />
                </div>
            </>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, data, count, search } = this.state
        const { navigate } = this.props
        const page = this.getPage()

        /* Check Status */
        if (status !== "ok") {
            return (
                <div className="dealer--page">
                    <InnerLayout inner status={status} reload={() => this.load(true)} />
                </div>
            )
        }

        return (
            <div className="dealer--page">

                {/* Head */}
                <div className="dealer--table-header">
                    <div className="dealer--column number">#</div>
                    <div className="dealer--column person">{t("dealer.name")}</div>
                    <div className="dealer--column pin">PIN / Barcode</div>
                    <div className="dealer--column studio">{t("dealer.studio")}</div>
                    <div className="dealer--column tables">{t("dealer.tables")}</div>
                    <div className="dealer--column actions" />
                </div>

                {/* Empty */}
                {data.length === 0 &&
                    <div className="dealer--empty">
                        <DealerIcon size={40} />
                        <span>{search !== "" ? t("dealer.nothingFound") : t("dealer.empty")}</span>
                    </div>
                }

                {/* Body */}
                {data.map((item, index) =>
                    <div className="dealer--row" key={`${index}`}>

                        <div className="dealer--column number">{(page - 1) * 10 + index + 1}</div>

                        <div className="dealer--column person">

                            <div className="dealer--avatar">{getInitials(item.name)}</div>

                            <div className="dealer--person-text">
                                <div className="dealer--person-name">{item.name}</div>
                                <div className="dealer--person-slug">{utils.checkValue(item.studioSlug, "—")}</div>
                            </div>

                        </div>

                        <div className="dealer--column pin">{this._pin(item)}</div>

                        <div className="dealer--column studio">{this._studio(item)}</div>

                        <div className="dealer--column tables">
                            <div className={`dealer--tables ${parseInt(item.gamecount) === 0 ? "empty" : ""}`}>
                                <TableIcon />
                                <span>{item.gamecount}</span>
                            </div>
                        </div>

                        <div className="dealer--column actions">{this._actions(item)}</div>

                    </div>
                )}

                {/* Pagination */}
                <Pagination size={10} page={page} total={count} onPageChange={(page) => navigate(`/dealer/${page}`)} />

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { status, canEdit, studios } = this.state

        return (
            <div className="page">

                <div className="page-header dealer--header">

                    <h1>{t("dealer.title")}</h1>

                    {status === "ok" &&
                        <div className="dealer--tools">

                            {canEdit &&
                                <div onClick={() => this._form.open({ type: "create" })} className="button dealer--add">
                                    <PlusIcon size={14} />
                                    <span>{t("common.add")}</span>
                                </div>
                            }

                            {/* Draw Search */}
                            {this._search()}

                            {/* Draw Studio Filter — a single studio scope has nothing to filter */}
                            {studios.length > 1 &&
                                <StudioFilter studios={studios} onApply={(list) => this.setState({ studios: list }, () => this.applyStudios())} />
                            }

                        </div>
                    }

                </div>

                {/* Draw Content */}
                {this._content()}

                {/* Draw Form Modal */}
                <DealerForm ref={(ref) => this._form = ref} reload={() => this.load()} />

                {/* Draw QR Modal */}
                <DealerQR ref={(ref) => this._qr = ref} />

            </div>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps)(withRouter(Dealers))
